package com.example.hukakmp.ui

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.FilterChip
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.example.hukakmp.model.AchievementCategory
import com.example.hukakmp.model.AchievementState
import com.example.hukakmp.viewmodel.AchievementsViewModel
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AchievementsScreen(
    uiState: AchievementsViewModel.AchievementsUiState,
    onBack: () -> Unit,
    onRefresh: () -> Unit,
) {
    var selectedCategory by remember { mutableStateOf<AchievementCategory?>(null) }
    var selectedAchievement by remember { mutableStateOf<AchievementState?>(null) }
    val shareAchievement = rememberAchievementSharer()

    val filtered = if (selectedCategory == null) {
        uiState.achievements
    } else {
        uiState.achievements.filter { it.definition.category == selectedCategory }
    }

    Scaffold(
        containerColor = MaterialTheme.colorScheme.background,
        topBar = {
            TopAppBar(
                title = {
                    Column {
                        Text("Mis Logros Huka", fontWeight = FontWeight.Bold)
                        Text(
                            "Coleccioná hitos de tus jornadas",
                            style = MaterialTheme.typography.labelSmall,
                            color = Color.White.copy(alpha = 0.76f),
                        )
                    }
                },
                navigationIcon = {
                    TextButton(onClick = onBack) {
                        Text("‹", color = Color.White, fontSize = 32.sp)
                    }
                },
                actions = {
                    Text("🏆", modifier = Modifier.padding(end = 18.dp), fontSize = 24.sp)
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = MaterialTheme.colorScheme.primary,
                    titleContentColor = MaterialTheme.colorScheme.onPrimary,
                ),
            )
        },
    ) { padding ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding),
        ) {
            AchievementProgressCard(uiState)

            CategoryFilters(
                selectedCategory = selectedCategory,
                onSelected = { selectedCategory = it },
            )

            when {
                uiState.isLoading && uiState.achievements.isEmpty() -> {
                    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            CircularProgressIndicator()
                            Text(
                                "Revisando tus jornadas…",
                                modifier = Modifier.padding(top = 12.dp),
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                            )
                        }
                    }
                }

                uiState.error != null && uiState.achievements.isEmpty() -> {
                    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Column(
                            modifier = Modifier.padding(28.dp),
                            horizontalAlignment = Alignment.CenterHorizontally,
                        ) {
                            Text("No pudimos cargar tus logros", fontWeight = FontWeight.Bold)
                            Text(
                                uiState.error,
                                modifier = Modifier.padding(top = 6.dp),
                                textAlign = TextAlign.Center,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                            )
                            TextButton(onClick = onRefresh, modifier = Modifier.padding(top = 8.dp)) {
                                Text("Reintentar")
                            }
                        }
                    }
                }

                else -> {
                    LazyVerticalGrid(
                        columns = GridCells.Fixed(2),
                        modifier = Modifier.fillMaxSize(),
                        contentPadding = PaddingValues(
                            start = 16.dp,
                            end = 16.dp,
                            top = 6.dp,
                            bottom = 24.dp,
                        ),
                        horizontalArrangement = Arrangement.spacedBy(12.dp),
                        verticalArrangement = Arrangement.spacedBy(12.dp),
                    ) {
                        items(filtered, key = { it.definition.id }) { achievement ->
                            AchievementStamp(
                                achievement = achievement,
                                onClick = { selectedAchievement = achievement },
                            )
                        }
                    }
                }
            }
        }
    }

    selectedAchievement?.let { achievement ->
        AchievementDetailDialog(
            achievement = achievement,
            onDismiss = { selectedAchievement = null },
            onShare = {
                shareAchievement(buildAchievementShareText(achievement))
            },
        )
    }
}

@Composable
private fun AchievementProgressCard(uiState: AchievementsViewModel.AchievementsUiState) {
    val total = uiState.totalCount.coerceAtLeast(1)
    val progress = uiState.unlockedCount / total.toFloat()

    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp, vertical = 14.dp),
        shape = RoundedCornerShape(22.dp),
        color = MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.58f),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(16.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Surface(
                modifier = Modifier.size(58.dp),
                shape = CircleShape,
                color = MaterialTheme.colorScheme.primary,
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Text("🏆", fontSize = 28.sp)
                }
            }

            Column(modifier = Modifier.weight(1f).padding(start = 14.dp)) {
                Row(verticalAlignment = Alignment.Bottom) {
                    Text(
                        uiState.unlockedCount.toString(),
                        style = MaterialTheme.typography.headlineMedium,
                        fontWeight = FontWeight.Black,
                        color = MaterialTheme.colorScheme.primary,
                    )
                    Text(
                        " / ${uiState.totalCount}",
                        modifier = Modifier.padding(bottom = 4.dp),
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
                Text(
                    "${uiState.points} puntos Huka",
                    style = MaterialTheme.typography.bodySmall,
                    fontWeight = FontWeight.Bold,
                )
                LinearProgressIndicator(
                    progress = { progress },
                    modifier = Modifier.fillMaxWidth().padding(top = 8.dp),
                    color = MaterialTheme.colorScheme.secondary,
                    trackColor = MaterialTheme.colorScheme.surfaceVariant,
                )
            }
        }
    }
}

@Composable
private fun CategoryFilters(
    selectedCategory: AchievementCategory?,
    onSelected: (AchievementCategory?) -> Unit,
) {
    val categories: List<AchievementCategory?> = listOf(null) + AchievementCategory.entries

    LazyRow(
        modifier = Modifier.fillMaxWidth(),
        contentPadding = PaddingValues(horizontal = 16.dp),
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        items(categories) { category ->
            FilterChip(
                selected = selectedCategory == category,
                onClick = { onSelected(category) },
                label = {
                    Text(if (category == null) "Todos" else "${category.emoji} ${category.label}")
                },
            )
        }
    }
    Spacer(Modifier.height(8.dp))
}

@Composable
private fun AchievementStamp(
    achievement: AchievementState,
    onClick: () -> Unit,
) {
    val unlocked = achievement.isUnlocked
    val definition = achievement.definition

    Card(
        modifier = Modifier
            .fillMaxWidth()
            .height(190.dp)
            .clickable(onClick = onClick),
        shape = RoundedCornerShape(20.dp),
        colors = CardDefaults.cardColors(
            containerColor = if (unlocked) {
                MaterialTheme.colorScheme.surface
            } else {
                MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.62f)
            },
        ),
        elevation = CardDefaults.cardElevation(defaultElevation = if (unlocked) 3.dp else 0.dp),
    ) {
        Column(
            modifier = Modifier.fillMaxSize().padding(14.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.SpaceBetween,
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    definition.category.label.uppercase(),
                    style = MaterialTheme.typography.labelSmall,
                    color = if (unlocked) {
                        MaterialTheme.colorScheme.primary
                    } else {
                        MaterialTheme.colorScheme.onSurfaceVariant
                    },
                    fontWeight = FontWeight.Bold,
                )
                Text(
                    if (unlocked) "+${definition.points}" else "🔒",
                    style = MaterialTheme.typography.labelSmall,
                )
            }

            Surface(
                modifier = Modifier.size(76.dp),
                shape = CircleShape,
                color = if (unlocked) {
                    MaterialTheme.colorScheme.secondaryContainer
                } else {
                    MaterialTheme.colorScheme.surfaceVariant
                },
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Text(
                        if (unlocked) definition.emoji else "🔒",
                        fontSize = if (unlocked) 38.sp else 28.sp,
                    )
                }
            }

            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                Text(
                    definition.title,
                    textAlign = TextAlign.Center,
                    fontWeight = FontWeight.Bold,
                    style = MaterialTheme.typography.titleSmall,
                    color = if (unlocked) {
                        MaterialTheme.colorScheme.onSurface
                    } else {
                        MaterialTheme.colorScheme.onSurfaceVariant
                    },
                )
                Text(
                    if (unlocked) "Desbloqueado · Compartible" else "Objetivo pendiente",
                    style = MaterialTheme.typography.labelSmall,
                    color = if (unlocked) {
                        MaterialTheme.colorScheme.secondary
                    } else {
                        MaterialTheme.colorScheme.onSurfaceVariant
                    },
                    modifier = Modifier.padding(top = 3.dp),
                )
            }
        }
    }
}

@Composable
private fun AchievementDetailDialog(
    achievement: AchievementState,
    onDismiss: () -> Unit,
    onShare: () -> Unit,
) {
    val definition = achievement.definition

    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                modifier = Modifier.fillMaxWidth(),
            ) {
                Text(if (achievement.isUnlocked) definition.emoji else "🔒", fontSize = 48.sp)
                Text(
                    definition.title,
                    modifier = Modifier.padding(top = 8.dp),
                    textAlign = TextAlign.Center,
                    fontWeight = FontWeight.Bold,
                )
            }
        },
        text = {
            Column {
                Text(
                    definition.description,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.fillMaxWidth(),
                )
                Surface(
                    modifier = Modifier.fillMaxWidth().padding(top = 14.dp),
                    shape = RoundedCornerShape(14.dp),
                    color = if (achievement.isUnlocked) {
                        MaterialTheme.colorScheme.secondaryContainer.copy(alpha = 0.6f)
                    } else {
                        MaterialTheme.colorScheme.surfaceVariant
                    },
                ) {
                    Column(modifier = Modifier.padding(12.dp)) {
                        Text(
                            if (achievement.isUnlocked) "✓ Logro desbloqueado" else "🔒 Todavía bloqueado",
                            fontWeight = FontWeight.Bold,
                        )
                        Text(
                            "${definition.category.emoji} ${definition.category.label} · ${definition.points} puntos",
                            style = MaterialTheme.typography.bodySmall,
                            modifier = Modifier.padding(top = 3.dp),
                        )
                        achievement.unlockedAtMillis?.takeIf { it > 0L }?.let { millis ->
                            Text(
                                "Obtenido el ${formatAchievementDate(millis)}",
                                style = MaterialTheme.typography.bodySmall,
                                modifier = Modifier.padding(top = 3.dp),
                            )
                        }
                    }
                }

                if (achievement.isUnlocked) {
                    Text(
                        "Compartilo con tus amigos y sumalos a Huka 🎣",
                        modifier = Modifier.fillMaxWidth().padding(top = 12.dp),
                        textAlign = TextAlign.Center,
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            }
        },
        dismissButton = {
            if (achievement.isUnlocked) {
                TextButton(onClick = onShare) {
                    Text("↗ Compartir logro")
                }
            }
        },
        confirmButton = {
            TextButton(onClick = onDismiss) { Text("Cerrar") }
        },
    )
}

private fun buildAchievementShareText(achievement: AchievementState): String {
    val definition = achievement.definition
    val dateLine = achievement.unlockedAtMillis
        ?.takeIf { it > 0L }
        ?.let { "\n📅 Obtenido el ${formatAchievementDate(it)}" }
        .orEmpty()

    return buildString {
        append("🏆 ¡Desbloqueé un logro en Huka!\n\n")
        append("${definition.emoji} ${definition.title}\n")
        append("${definition.description}\n\n")
        append("⭐ ${definition.points} puntos Huka")
        append(dateLine)
        append("\n\n🎣 Registrando nuestras jornadas conocemos mejor la pesca recreativa.\n")
        append("#Huka #PescaRecreativa")
    }
}

private fun formatAchievementDate(millis: Long): String {
    val date = kotlin.time.Instant.fromEpochMilliseconds(millis)
        .toLocalDateTime(TimeZone.currentSystemDefault())
        .date
    return "${date.dayOfMonth.toString().padStart(2, '0')}/${date.monthNumber.toString().padStart(2, '0')}/${date.year}"
}
